/**
 * ErrorToast Component
 * 
 * Auto-dismissing toast notification for displaying error messages.
 * Appears at the top-right of the viewport and fades out after a configurable duration.
 * 
 * @component
 * @example
 * <ErrorToast
 *   message="Failed to load data. Showing previous results."
 *   onDismiss={() => setToast(null)}
 *   duration={4000}
 * />
 */

import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * ErrorToast - Auto-dismissing error notification
 * 
 * Renders a fixed-position toast with a fade-in/fade-out animation.
 * Automatically dismisses after the specified duration.
 * Includes a manual close button for immediate dismissal.
 * 
 * @param {Object} props - Component props
 * @param {string} props.message - Error message to display
 * @param {Function} props.onDismiss - Callback when toast is dismissed (auto or manual)
 * @param {number} [props.duration=4000] - Auto-dismiss duration in milliseconds
 * @returns {JSX.Element|null} Toast notification or null if fading out complete
 */
function ErrorToast({ message, onDismiss, duration = 4000 }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  /** Trigger fade-out then call onDismiss */
  const dismiss = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(() => {
      onDismiss();
    }, 300); // Match fade-out animation duration
  }, [onDismiss]);

  // Fade in on mount
  useEffect(() => {
    // Small delay to trigger CSS transition
    const showTimer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(showTimer);
  }, []);

  // Auto-dismiss after duration
  useEffect(() => {
    const timer = setTimeout(dismiss, duration);
    return () => clearTimeout(timer);
  }, [duration, dismiss]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        maxWidth: 380,
        minWidth: 280,
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)',
        border: '1px solid #fca5a5',
        borderRadius: 10,
        color: '#ffffff',
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.5,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        opacity: isVisible && !isFadingOut ? 1 : 0,
        transform: isVisible && !isFadingOut ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: 'auto',
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* Error icon */}
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fca5a5"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0, marginTop: 1 }}
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>

      {/* Message text */}
      <span style={{ flex: 1 }}>{message}</span>

      {/* Close button */}
      <button
        onClick={dismiss}
        aria-label="Dismiss error notification"
        style={{
          background: 'none',
          border: 'none',
          color: '#fca5a5',
          cursor: 'pointer',
          padding: 2,
          lineHeight: 1,
          flexShrink: 0,
          opacity: 0.8,
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.8'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

ErrorToast.propTypes = {
  /** Error message text to display in the toast */
  message: PropTypes.string.isRequired,
  /** Callback function called when the toast is dismissed */
  onDismiss: PropTypes.func.isRequired,
  /** Duration in milliseconds before auto-dismissing (default: 4000) */
  duration: PropTypes.number,
};

export default ErrorToast;
